export type DocStatus = "ga" | "pilot" | "preview" | "draft";

export type PublicDocEntry = {
	path: string;
	route: string;
	status: DocStatus;
	title: string;
};

/** Relative to this file → repo `docs/public/` (not `client/docs/`). */
const PUBLIC_DOCS_PREFIX = "../../../docs/public";

const rawModules = import.meta.glob("../../../docs/public/**/*.md", {
	query: "?raw",
	import: "default",
	eager: true,
}) as Record<string, string>;

const ROUTE_TABLE: { route: string; file: string; status: DocStatus }[] = [
	{ route: "/help", file: "teams/README.md", status: "ga" },
	{ route: "/help/getting-started", file: "teams/getting-started.md", status: "ga" },
	{ route: "/help/running-a-session", file: "teams/running-a-session.md", status: "ga" },
	{ route: "/help/consensus-and-charts", file: "teams/consensus-and-charts.md", status: "ga" },
	{ route: "/help/slack", file: "teams/using-in-slack.md", status: "pilot" },
	{ route: "/help/jira", file: "teams/using-in-jira.md", status: "pilot" },
	{ route: "/help/clickup", file: "teams/using-in-clickup.md", status: "pilot" },
	{ route: "/help/asana", file: "teams/using-in-asana.md", status: "pilot" },
	{ route: "/help/teams", file: "teams/using-in-teams.md", status: "preview" },
	{ route: "/help/admin", file: "admin/faq.md", status: "ga" },
	{ route: "/help/troubleshooting", file: "admin/troubleshooting.md", status: "ga" },
	{ route: "/help/admin/uat-install", file: "admin/uat-install/README.md", status: "pilot" },
	{ route: "/help/admin/uat-install/slack", file: "admin/uat-install/slack.md", status: "pilot" },
	{ route: "/help/admin/uat-install/jira", file: "admin/uat-install/jira.md", status: "pilot" },
	{ route: "/help/admin/uat-install/clickup", file: "admin/uat-install/clickup.md", status: "pilot" },
	{ route: "/help/admin/uat-install/asana", file: "admin/uat-install/asana.md", status: "pilot" },
	{ route: "/help/admin/uat-install/web", file: "admin/uat-install/web.md", status: "ga" },
	{ route: "/help/install", file: "platform-owners/README.md", status: "ga" },
	{ route: "/help/install/web", file: "platform-owners/web.md", status: "ga" },
	{ route: "/help/install/slack", file: "platform-owners/slack.md", status: "pilot" },
	{ route: "/help/install/jira", file: "platform-owners/jira.md", status: "pilot" },
	{ route: "/help/install/clickup", file: "platform-owners/clickup.md", status: "pilot" },
	{ route: "/help/install/asana", file: "platform-owners/asana.md", status: "pilot" },
	{ route: "/help/install/microsoft-teams", file: "platform-owners/microsoft-teams.md", status: "preview" },
	{ route: "/trust", file: "trust/security-overview.md", status: "ga" },
	{ route: "/trust/security", file: "trust/security-overview.md", status: "ga" },
	{ route: "/trust/privacy", file: "trust/privacy-policy-summary.md", status: "draft" },
	{ route: "/trust/data-processing", file: "trust/data-processing.md", status: "draft" },
	{ route: "/trust/compliance", file: "trust/compliance.md", status: "draft" },
	{ route: "/trust/vulnerability-disclosure", file: "trust/vulnerability-disclosure.md", status: "draft" },
];

function moduleKey(file: string): string {
	return `${PUBLIC_DOCS_PREFIX}/${file}`;
}

function readDocSource(file: string): string {
	const exact = rawModules[moduleKey(file)];
	if (exact) return exact;
	// Vite may normalize keys; match by public-docs suffix as a fallback.
	const suffix = `/docs/public/${file}`;
	for (const [key, source] of Object.entries(rawModules)) {
		if (key.endsWith(suffix) || key.endsWith(`/${file}`)) return source;
	}
	return "";
}

function titleFromMarkdown(source: string, fallback: string): string {
	const match = source.match(/^#\s+(.+)$/m);
	return match?.[1]?.trim() ?? fallback;
}

export const PUBLIC_DOC_ENTRIES: PublicDocEntry[] = ROUTE_TABLE.map(({ route, file, status }) => {
	const source = readDocSource(file);
	return {
		route,
		path: file,
		status,
		title: titleFromMarkdown(source, route),
	};
});

export function resolvePublicDoc(pathname: string): PublicDocEntry & { source: string } | null {
	const normalized = pathname.endsWith("/") && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
	const row = ROUTE_TABLE.find((r) => r.route === normalized);
	if (!row) return null;
	const source = readDocSource(row.file);
	if (!source) return null;
	return {
		route: row.route,
		path: row.file,
		status: row.status,
		title: titleFromMarkdown(source, row.route),
		source,
	};
}

export const HELP_NAV = [
	{ to: "/help/getting-started", label: "Getting started" },
	{ to: "/help/running-a-session", label: "Running a session" },
	{ to: "/help/consensus-and-charts", label: "Consensus & charts" },
	{ to: "/help/install", label: "Install guides" },
	{ to: "/help/admin/uat-install", label: "Connect on staging" },
	{ to: "/help/admin", label: "Admin FAQ" },
	{ to: "/help/troubleshooting", label: "Troubleshooting" },
] as const;

export const TRUST_NAV = [
	{ to: "/trust/security", label: "Security" },
	{ to: "/trust/privacy", label: "Privacy" },
	{ to: "/trust/data-processing", label: "Data processing" },
	{ to: "/trust/compliance", label: "Compliance" },
	{ to: "/trust/vulnerability-disclosure", label: "Disclosure" },
] as const;
